import { useState, useEffect } from 'react';
import { reprintApi, spacesApi } from '../services/api';
import { formatCurrency, formatDateTime, formatApiError } from '../utils/helpers';
import { X, Printer, MagnifyingGlass, MapPin, Receipt, Keyboard } from '@phosphor-icons/react';

export default function ReprintModal({ onClose, onPrint }) {
  const [mode, setMode] = useState('space'); // 'space' or 'ticket'
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [ticketId, setTicketId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadSpaces();
  }, []);

  const loadSpaces = async () => {
    try {
      const res = await spacesApi.getAll();
      setSpaces(res.data);
    } catch {}
  };

  const handleSpaceSelect = async (space) => {
    setSelectedSpace(space);
    setTicketData(null);
    setError('');
    setLoading(true);
    try {
      const res = await reprintApi.bySpace(space.label);
      if (res.data.found) {
        setTicketData(res.data);
      } else {
        setError('No se encontro ticket para este espacio');
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleTicketSearch = async () => {
    if (!ticketId.trim()) { setError('Ingrese el ID del ticket'); return; }
    setLoading(true);
    setError('');
    setTicketData(null);
    try {
      const res = await reprintApi.getTicket(ticketId.trim());
      setTicketData(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (ticketData) {
      onPrint({ type: ticketData.type, data: ticketData });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" data-testid="reprint-modal">
      <div className="bg-[#141414] rounded-xl w-full max-w-lg border border-[#27272A]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#27272A]">
          <h2 className="text-xl font-heading text-white">Reimprimir Ticket</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#27272A] rounded-lg transition-colors" data-testid="reprint-close">
            <X className="w-6 h-6 text-[#A1A1AA]" weight="bold" />
          </button>
        </div>

        {/* Mode Tabs */}
        <div className="flex border-b border-[#27272A]">
          <button onClick={() => { setMode('space'); setTicketData(null); setError(''); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${mode === 'space' ? 'text-[#4ADE80] border-b-2 border-[#4ADE80]' : 'text-[#A1A1AA] hover:text-white'}`}
            data-testid="reprint-mode-space">
            <MapPin className="w-4 h-4" weight="bold" />
            Por Espacio
          </button>
          <button onClick={() => { setMode('ticket'); setTicketData(null); setError(''); setSelectedSpace(null); }}
            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${mode === 'ticket' ? 'text-[#4ADE80] border-b-2 border-[#4ADE80]' : 'text-[#A1A1AA] hover:text-white'}`}
            data-testid="reprint-mode-ticket">
            <Keyboard className="w-4 h-4" weight="bold" />
            Por Ticket ID
          </button>
        </div>

        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="bg-[#450A0A] border border-[#DC2626] text-[#FCA5A5] px-4 py-3 rounded-lg text-sm" data-testid="reprint-error">
              {error}
            </div>
          )}

          {/* Space Selection Mode */}
          {mode === 'space' && !ticketData && (
            <>
              <p className="text-[#A1A1AA] text-sm">Seleccione un espacio para buscar su ticket:</p>
              <div className="grid grid-cols-4 gap-2">
                {spaces.map((space) => {
                  const isOccupied = space.status === 'occupied';
                  const isSelected = selectedSpace?.id === space.id;
                  return (
                    <button key={space.id}
                      onClick={() => handleSpaceSelect(space)}
                      disabled={loading}
                      className={`p-3 rounded-lg text-center transition-colors border ${
                        isSelected ? 'border-[#4ADE80] bg-[#052E16]' :
                        isOccupied ? 'border-[#DC2626]/30 bg-[#450A0A]/30 hover:bg-[#450A0A]/60' :
                        'border-[#27272A] bg-[#1C1C1E] hover:bg-[#27272A]'
                      }`}
                      data-testid={`reprint-space-${space.label}`}>
                      <div className={`font-mono font-bold text-sm ${isOccupied ? 'text-[#FCA5A5]' : 'text-[#A1A1AA]'}`}>
                        {space.label}
                      </div>
                      {isOccupied && (
                        <div className="text-[10px] text-[#FCA5A5] mt-0.5 truncate">
                          {space.vehicle?.plate_number}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              {loading && <p className="text-[#A1A1AA] text-sm text-center">Buscando ticket...</p>}
            </>
          )}

          {/* Ticket ID Mode */}
          {mode === 'ticket' && !ticketData && (
            <>
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2 uppercase tracking-wide">ID del Ticket</label>
                <div className="relative">
                  <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" weight="bold" />
                  <input type="text" value={ticketId}
                    onChange={(e) => setTicketId(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && handleTicketSearch()}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg pl-12 pr-4 py-4 text-white text-lg font-mono uppercase tracking-wider focus:border-[#16A34A] focus:outline-none transition-colors"
                    placeholder="TK20260101..."
                    data-testid="reprint-ticket-input"
                  />
                </div>
              </div>
              <button onClick={handleTicketSearch} disabled={loading} className="w-full btn-primary text-lg" data-testid="reprint-ticket-search-btn">
                {loading ? 'Buscando...' : <span className="flex items-center justify-center gap-2"><MagnifyingGlass className="w-5 h-5" weight="bold" />Buscar</span>}
              </button>
            </>
          )}

          {/* Ticket Preview (shown after finding a ticket) */}
          {ticketData && (
            <div className="space-y-4">
              <div className="bg-[#1C1C1E] rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="w-5 h-5 text-[#4ADE80]" weight="bold" />
                  <span className="text-white font-medium">
                    {ticketData.type === 'entry' ? 'Ticket de Entrada' : 'Ticket de Salida'}
                  </span>
                  <span className={`px-2 py-0.5 text-xs rounded ${
                    ticketData.type === 'entry' ? 'bg-[#052E16] text-[#4ADE80]' : 'bg-[#422006] text-[#FCD34D]'
                  }`}>
                    {ticketData.type === 'entry' ? 'ACTIVO' : 'COMPLETADO'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <span className="text-[#A1A1AA]">Ticket:</span>
                  <span className="text-white font-mono text-xs">{ticketData.ticket_id}</span>
                  <span className="text-[#A1A1AA]">Espacio:</span>
                  <span className="text-white font-bold">{ticketData.space_label}</span>
                  <span className="text-[#A1A1AA]">Patente:</span>
                  <span className="text-white font-mono">{ticketData.plate_number}</span>
                  <span className="text-[#A1A1AA]">Tipo:</span>
                  <span className="text-white">{ticketData.vehicle_type}</span>
                  <span className="text-[#A1A1AA]">Entrada:</span>
                  <span className="text-white font-mono text-xs">{formatDateTime(ticketData.entry_time)}</span>
                  {ticketData.type === 'exit' && (
                    <>
                      <span className="text-[#A1A1AA]">Salida:</span>
                      <span className="text-white font-mono text-xs">{formatDateTime(ticketData.exit_time)}</span>
                      <span className="text-[#A1A1AA]">Total:</span>
                      <span className="text-[#4ADE80] font-mono font-bold">{formatCurrency(ticketData.total_price)}</span>
                    </>
                  )}
                </div>
              </div>

              <button onClick={handlePrint} className="w-full btn-success text-lg" data-testid="reprint-print-btn">
                <span className="flex items-center justify-center gap-2">
                  <Printer className="w-5 h-5" weight="bold" />
                  Reimprimir {ticketData.type === 'entry' ? 'Entrada' : 'Salida'}
                </span>
              </button>

              <button onClick={() => { setTicketData(null); setSelectedSpace(null); setError(''); }}
                className="w-full btn-secondary" data-testid="reprint-back-btn">
                Buscar otro
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
